/**
 * @file Recalls frontend API wrappers for backend-authoritative callables.
 */

import {
    advancedSearchRecallsCallable as configuredAdvancedSearchRecallsCallable,
    completeRecallStatusWorkflowCallable as configuredCompleteRecallStatusWorkflowCallable,
    createRecallEntryCallable as configuredCreateRecallEntryCallable,
    deleteRecallDraftCallable as configuredDeleteRecallDraftCallable,
    exportRecallsCallable as configuredExportRecallsCallable,
    getRecallImportMappingMemoryCallable as configuredGetRecallImportMappingMemoryCallable,
    importRecallsCsvCallable as configuredImportRecallsCsvCallable,
    promoteRecallDraftCallable as configuredPromoteRecallDraftCallable,
    saveRecallDraftCallable as configuredSaveRecallDraftCallable,
    updateRecallDetailsCallable as configuredUpdateRecallDetailsCallable,
    updateRecallEntryCallable as configuredUpdateRecallEntryCallable,
} from "../../app/firebase/callables.js";

function unwrapCallableResult(result) {
    return result?.data || {};
}

/**
 * Creates Recalls API helpers.
 * @param {object} deps Callable dependency bag.
 * @returns {object} Recalls API helpers.
 */
export function createRecallsApi({
    advancedSearchRecallsCallable,
    completeRecallStatusWorkflowCallable,
    createRecallEntryCallable,
    deleteRecallDraftCallable,
    exportRecallsCallable,
    getRecallImportMappingMemoryCallable,
    importRecallsCsvCallable,
    promoteRecallDraftCallable,
    saveRecallDraftCallable,
    updateRecallDetailsCallable,
    updateRecallEntryCallable,
}) {
    return {
        /**
         * Saves a shared Recall draft.
         * @param {object} params Save params.
         * @returns {Promise<object>} Callable result payload.
         */
        async saveRecallDraft(params = {}) {
            return unwrapCallableResult(await saveRecallDraftCallable(params));
        },

        /**
         * Deletes a shared Recall draft.
         * @param {object} params Delete params.
         * @returns {Promise<object>} Callable result payload.
         */
        async deleteRecallDraft(params = {}) {
            return unwrapCallableResult(await deleteRecallDraftCallable(params));
        },

        /**
         * Creates a permanent Recall entry.
         * @param {object} params Create params.
         * @returns {Promise<object>} Callable result payload.
         */
        async createRecallEntry(params = {}) {
            return unwrapCallableResult(await createRecallEntryCallable(params));
        },

        /**
         * Promotes a shared Recall draft to a permanent entry.
         * @param {object} params Promotion params.
         * @returns {Promise<object>} Callable result payload.
         */
        async promoteRecallDraft(params = {}) {
            return unwrapCallableResult(await promoteRecallDraftCallable(params));
        },

        /**
         * Updates key detail fields for a permanent Recall entry.
         * @param {object} params Update params.
         * @returns {Promise<object>} Callable result payload.
         */
        async updateRecallDetails(params = {}) {
            return unwrapCallableResult(await updateRecallDetailsCallable(params));
        },

        /**
         * Updates inline-editable Recall entry fields.
         * @param {object} params Update params.
         * @returns {Promise<object>} Callable result payload.
         */
        async updateRecallEntry(params = {}) {
            return unwrapCallableResult(await updateRecallEntryCallable(params));
        },

        /**
         * Completes a status-driven Recall workflow.
         * @param {object} params Workflow params.
         * @returns {Promise<object>} Callable result payload.
         */
        async completeRecallStatusWorkflow(params = {}) {
            return unwrapCallableResult(
                await completeRecallStatusWorkflowCallable(params),
            );
        },

        /**
         * Exports selected Recall entries to the marketing CSV format.
         * @param {object} params Export params.
         * @returns {Promise<object>} Callable result payload.
         */
        async exportRecalls(params = {}) {
            return unwrapCallableResult(await exportRecallsCallable(params));
        },

        /**
         * Imports Recalls rows from admin-reviewed CSV text.
         * @param {object} params Import params.
         * @returns {Promise<object>} Callable result payload.
         */
        async importRecallsCsv(params = {}) {
            return unwrapCallableResult(await importRecallsCsvCallable(params));
        },

        /**
         * Loads saved Recalls import mapping memory.
         * @param {object} params Mapping lookup params.
         * @returns {Promise<object>} Callable result payload.
         */
        async getRecallImportMappingMemory(params = {}) {
            return unwrapCallableResult(
                await getRecallImportMappingMemoryCallable(params),
            );
        },

        /**
         * Runs a server-backed Recalls advanced search.
         * @param {object} params Search params.
         * @returns {Promise<object>} Callable result payload.
         */
        async advancedSearchRecalls(params = {}) {
            return unwrapCallableResult(
                await advancedSearchRecallsCallable(params),
            );
        },
    };
}

const configuredRecallsApi = createRecallsApi({
    advancedSearchRecallsCallable: configuredAdvancedSearchRecallsCallable,
    completeRecallStatusWorkflowCallable:
        configuredCompleteRecallStatusWorkflowCallable,
    createRecallEntryCallable: configuredCreateRecallEntryCallable,
    deleteRecallDraftCallable: configuredDeleteRecallDraftCallable,
    exportRecallsCallable: configuredExportRecallsCallable,
    getRecallImportMappingMemoryCallable:
        configuredGetRecallImportMappingMemoryCallable,
    importRecallsCsvCallable: configuredImportRecallsCsvCallable,
    promoteRecallDraftCallable: configuredPromoteRecallDraftCallable,
    saveRecallDraftCallable: configuredSaveRecallDraftCallable,
    updateRecallDetailsCallable: configuredUpdateRecallDetailsCallable,
    updateRecallEntryCallable: configuredUpdateRecallEntryCallable,
});

export const saveRecallDraft = configuredRecallsApi.saveRecallDraft;
export const deleteRecallDraft = configuredRecallsApi.deleteRecallDraft;
export const createRecallEntry = configuredRecallsApi.createRecallEntry;
export const promoteRecallDraft = configuredRecallsApi.promoteRecallDraft;
export const updateRecallDetails = configuredRecallsApi.updateRecallDetails;
export const updateRecallEntry = configuredRecallsApi.updateRecallEntry;
export const completeRecallStatusWorkflow =
    configuredRecallsApi.completeRecallStatusWorkflow;
export const exportRecalls = configuredRecallsApi.exportRecalls;
export const importRecallsCsv = configuredRecallsApi.importRecallsCsv;
export const getRecallImportMappingMemory =
    configuredRecallsApi.getRecallImportMappingMemory;
export const advancedSearchRecalls =
    configuredRecallsApi.advancedSearchRecalls;
